import { useRef, useState } from "react";
import {
  Box,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  IconButton,
  LinearProgress,
  Alert,
  Tooltip,
} from "@mui/material";

import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AddIcon from "@mui/icons-material/Add";

export interface StagedFileView {
  id: string;
  name: string;
  size: string;
  status: "idle" | "uploading" | "uploaded" | "error";
  progress: number;
  error?: string;
}

interface FileUploadTableProps {
  title: string;
  allowedFiles: string;
  files: StagedFileView[];
  disabled?: boolean;
  error?: string;
  onFilesSelected: (files: File[]) => void;
  onDelete: (id: string) => void;
}

const FileUploadTable = ({
  title,
  allowedFiles,
  files,
  disabled = false,
  error,
  onFilesSelected,
  onDelete,
}: FileUploadTableProps) => {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (disabled) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onFilesSelected(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onFilesSelected(Array.from(e.target.files));
    }
    e.target.value = "";
  };

  const openFilePicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  return (
    <Stack spacing={1}>
      {title && (
        <Typography variant="body2" fontWeight="bold">
          {title}
        </Typography>
      )}
      <input
        type="file"
        multiple
        accept={allowedFiles}
        ref={inputRef}
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />
      <Paper
        variant="outlined"
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        onClick={files.length === 0 ? openFilePicker : undefined}
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 200,
          maxHeight: 280,
          overflow: "hidden",
          backgroundColor: dragActive ? "action.hover" : "background.paper",
          border: dragActive ? "2px dashed" : "1px solid",
          borderColor: dragActive ? "primary.main" : "divider",
          transition: "all 0.2s",
          cursor: files.length === 0 && !disabled ? "pointer" : "default",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {files.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 4,
              minHeight: 200,
            }}
          >
            <CloudUploadIcon
              sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              Arraste arquivos aqui ou clique para selecionar
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer
              sx={{
                flex: 1,
                minHeight: 0,
                overflowX: "hidden",
                overflowY: "auto",
              }}
            >
              <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Arquivo</TableCell>
                    <TableCell align="right" sx={{ width: 56 }}>
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {files.map((file) => (
                    <TableRow key={file.id} hover>
                      <TableCell sx={{ overflow: "hidden" }}>
                        <Stack spacing={0.5}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ minWidth: 0, overflow: "hidden" }}
                          >
                            <InsertDriveFileIcon
                              color="secondary"
                              sx={{ fontSize: 20, flexShrink: 0 }}
                            />
                            <Typography
                              variant="caption"
                              title={file.name}
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                minWidth: 0,
                                flex: 1,
                              }}
                            >
                              {file.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ flexShrink: 0 }}
                            >
                              ({file.size})
                            </Typography>
                            {file.status === "uploaded" && (
                              <Tooltip title="Enviado com sucesso">
                                <CheckCircleOutlineIcon
                                  color="success"
                                  sx={{ fontSize: 16, flexShrink: 0 }}
                                />
                              </Tooltip>
                            )}
                            {file.status === "error" && (
                              <Tooltip title={file.error || "Falha no envio"}>
                                <ErrorOutlineIcon
                                  color="error"
                                  sx={{ fontSize: 16, flexShrink: 0 }}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                          {file.status === "uploading" && (
                            <LinearProgress
                              variant="determinate"
                              value={file.progress}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          disabled={disabled || file.status === "uploading"}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(file.id);
                          }}
                        >
                          <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {!disabled && (
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  openFilePicker();
                }}
                sx={{
                  flexShrink: 0,
                  cursor: "pointer",
                  borderTop: "1px dashed",
                  borderColor: "divider",
                  px: 2,
                  py: 0.75,
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{ color: "text.secondary" }}
                >
                  <AddIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption">Adicionar arquivo</Typography>
                </Stack>
              </Box>
            )}
          </>
        )}
      </Paper>
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
};

export default FileUploadTable;
